// Card - displays individual card with edit/delete functionality
// Implements US-2.1: Card display, editing, and ownership validation
// Implements US-3.1: Permission restrictions during facilitation phase
// Implements US-2.4: Expand/collapse card text

import React, { useState } from 'react';
import { Pencil, Trash2, Check, Circle } from 'lucide-react';
import { Card as CardType } from '../types/card';
import { CardForm } from './CardForm';
import { VoteButton } from './VoteButton';
import styles from './Card.module.css';

interface CardProps {
  card: CardType;
  isOwner: boolean;
  isCollecting: boolean;
  isFacilitator: boolean;
  onUpdate: (cardId: string, text: string) => void;
  onDelete: (cardId: string) => void;
  onToggleExpand: (cardId: string) => void;
  onToggleDiscussed: (cardId: string) => void;
  isSelected?: boolean;
  onToggleSelect?: (cardId: string) => void;
  showSelect?: boolean; // Show checkbox for grouping
  votingActive?: boolean; // US-3.2: Is voting currently active?
  dotsRemaining?: number; // US-3.2: Dots remaining for this user
  onVote?: (cardId: string, dots: number) => void; // US-3.2: Cast vote callback
}

export const Card: React.FC<CardProps> = ({
  card,
  isOwner,
  isCollecting,
  isFacilitator,
  onUpdate,
  onDelete,
  onToggleExpand,
  onToggleDiscussed,
  isSelected = false,
  onToggleSelect,
  showSelect = false,
  votingActive = false,
  dotsRemaining = 0,
  onVote,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // HANDLE edit submit
  const handleUpdate = (text: string) => {
    onUpdate(card.id, text);
    setIsEditing(false);
  };

  // HANDLE edit cancel
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // HANDLE delete with confirmation
  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  // CONFIRM delete
  const confirmDelete = () => {
    onDelete(card.id);
    setShowDeleteConfirm(false);
  };

  // CANCEL delete
  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  // RENDER edit form if editing
  if (isEditing) {
    return (
      <div className={`${styles.card} ${styles.editing}`}>
        <CardForm
          initialText={card.text}
          onSubmit={handleUpdate}
          onCancel={handleCancelEdit}
          submitLabel="Speichern"
        />
      </div>
    );
  }

  // RENDER delete confirmation if confirming
  if (showDeleteConfirm) {
    return (
      <div className={`${styles.card} ${styles.deleteConfirm}`}>
        <p className={styles.confirmMessage}>Diese Karte löschen?</p>
        <div className={styles.confirmActions}>
          <button className={styles.btnDanger} onClick={confirmDelete}>
            Löschen
          </button>
          <button className={styles.btnDanger} onClick={cancelDelete}>
            Abbrechen
          </button>
        </div>
      </div>
    );
  }

  // TRUNCATE text for collapsed view (first line, ~80 chars max)
  const getDisplayText = () => {
    if (card.expanded) {
      return card.text;
    }
    // SHOW first line only in collapsed view
    const firstLine = card.text.split('\n')[0];
    if (firstLine.length > 80) {
      return firstLine.substring(0, 80) + '...';
    }
    return card.text.length > 80 ? firstLine + '...' : firstLine;
  };

  // HANDLE card click to toggle expand/collapse
  const handleCardClick = (e: React.MouseEvent) => {
    // PREVENT toggle when clicking buttons, checkbox, or during editing
    if (
      isEditing ||
      showDeleteConfirm ||
      (e.target as HTMLElement).closest('.card-actions') ||
      (e.target as HTMLElement).closest('.card-select-checkbox') ||
      (e.target as HTMLElement).closest('button')
    ) {
      return;
    }
    onToggleExpand(card.id);
  };

  // HANDLE drag start (only for facilitators during facilitation)
  const handleDragStart = (e: React.DragEvent) => {
    if (!isFacilitator || isCollecting) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('cardId', card.id);
    e.dataTransfer.setData('sourceColumn', card.column);
  };

  // RENDER card display
  return (
    <div
      className={`${styles.card} ${isOwner ? styles.ownCard : ''} ${card.expanded ? styles.expanded : styles.collapsed} ${isSelected ? styles.selected : ''}`}
      data-card-id={card.id}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
      draggable={isFacilitator && !isCollecting}
      onDragStart={handleDragStart}
    >
      {/* Selection checkbox for grouping */}
      {showSelect && onToggleSelect && (
        <div className={styles.cardSelectCheckbox}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(card.id)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className={styles.cardContent}>
        <p className={styles.cardText}>{getDisplayText()}</p>

        <div className={styles.cardMetadata}>
          {card.authorName && (
            <div className={styles.cardAuthor}>— {card.authorName}</div>
          )}

          {/* US-2.3: Action item metadata (owner + due date) */}
          {card.column === 'actions' && (card.owner || card.dueDate) && (
            <div className={styles.actionItemMetadata}>
              {card.owner && (
                <div className={styles.actionOwner}>
                  <span className={styles.label}>Verantwortlich:</span> {card.owner}
                </div>
              )}
              {card.dueDate && (
                <div className={styles.actionDueDate}>
                  <span className={styles.label}>Fällig:</span> {new Date(card.dueDate).toLocaleDateString()}
                </div>
              )}
            </div>
          )}

          {/* US-3.2: Vote button/badge (not for action items) */}
          {onVote && card.column !== 'actions' && (
            <VoteButton
              cardId={card.id}
              voteCount={card.voteCount}
              votingActive={votingActive}
              dotsRemaining={dotsRemaining}
              onVote={onVote}
            />
          )}

          {/* US-3.3: Discussed badge (clickable by facilitator during facilitation) */}
          {(card.discussed || (isFacilitator && !isCollecting)) && (
            <div
              className={`${styles.cardDiscussed} ${isFacilitator && !isCollecting ? styles.clickable : ''}`}
              onClick={(e) => {
                if (isFacilitator && !isCollecting) {
                  e.stopPropagation();
                  onToggleDiscussed(card.id);
                }
              }}
              title={isFacilitator && !isCollecting ? 'Klicken um Status zu ändern' : undefined}
            >
              <span className={`${styles.discussedBadge} ${card.discussed ? styles.active : ''}`}>
                {card.discussed ? <><Check size={14} /> Besprochen</> : <><Circle size={14} /> Nicht besprochen</>}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* SHOW edit/delete buttons based on phase and permissions */}
      {/* During collecting: only card owner can edit/delete */}
      {/* During facilitation: only facilitator can edit/delete */}
      {((isOwner && isCollecting) || (!isCollecting && isFacilitator)) && (
        <div className={styles.cardActions}>
          <button
            className={styles.btnIcon}
            onClick={() => setIsEditing(true)}
            title="Karte bearbeiten"
          >
            <Pencil size={16} />
          </button>
          <button
            className={styles.btnIcon}
            onClick={handleDelete}
            title="Karte löschen"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}
    </div>
  );
};
